import React, { useState } from 'react';
import { useAuditLogs } from '../../hooks/useAuditLogs';
import { AuditLogTable } from './components/AuditLogTable';
import { AuditLogFiltersPanel } from './components/AuditLogFilters';
import { AuditLogDetailsModal } from './components/AuditLogDetailsModal';
import { auditApi } from '../../services/api';
import { AuditLogDto } from '../../types/audit';
import { usePageConfig } from '../../contexts/PageConfigContext';

export function AuditLogsPage() {
  const [selectedLog, setSelectedLog] = useState<AuditLogDto | null>(null);
  
  const {
    logs,
    pagination,
    filters,
    loading,
    error,
    updateFilters,
    goToPage,
  } = useAuditLogs({});

  // Set page configuration
  const { setPageConfig } = usePageConfig();

  React.useEffect(() => {
    setPageConfig({
      title: 'Logs de Auditoría',
      description: 'Visualiza y filtra los registros de auditoría del sistema',
    });
  }, [setPageConfig]);

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const blob = await auditApi.exportLogs(
        { ...filters, format }
      );
      const filename = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      auditApi.downloadExport(blob, filename);
    } catch (err) {
      console.error('Error al exportar:', err);
      alert('Error al exportar los logs. Por favor, intenta de nuevo.');
    }
  };

  const handleViewDetails = async (log: AuditLogDto) => {
    try {
      // Obtener detalles completos con oldValues/newValues
      const fullLog = await auditApi.getLogById(log.id);
      setSelectedLog(fullLog);
    } catch (err) {
      console.error('Error al obtener detalles:', err);
      alert('Error al cargar los detalles del log. Por favor, intenta de nuevo.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Logs de Auditoría</h1>

      {/* Filtros */}
      <AuditLogFiltersPanel
        filters={filters}
        onFiltersChange={updateFilters}
        onExport={handleExport}
      />

      {/* Error */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
          <strong className="font-bold">Error: </strong>
          <span>{error}</span>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow">
        <AuditLogTable
          logs={logs}
          loading={loading}
          onViewDetails={handleViewDetails}
        />

        {/* Paginación */}
        {!loading && logs.length > 0 && (
          <div className="px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-700">
              Mostrando {((pagination.page - 1) * pagination.pageSize) + 1} a{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} de{' '}
              {pagination.totalItems} resultados
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-gray-700">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de detalles */}
      <AuditLogDetailsModal
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
}
